import { useState, useEffect, useRef, useCallback } from 'react';
import JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';

export type PhoneStatus = 'idle' | 'registering' | 'registered' | 'failed' | 'in_call' | 'disconnected';

export interface PhoneConfig {
  sipUri: string;
  sipPassword: string;
  wsServer: string;
  sipDomain: string;
  displayName?: string;
  vpnMode?: boolean;
  stunServer?: string;
  turnServer?: string;
  turnUser?: string;
  turnPass?: string;
}

export interface IncomingCallInfo {
  session: RTCSession;
  callerNumber: string;
}

interface UsePhoneOptions {
  config: PhoneConfig | null;
  onIncomingCall?: (session: RTCSession, callerNumber: string) => void;
  remoteAudioRef?: React.MutableRefObject<HTMLAudioElement | null>;
}

interface UsePhoneReturn {
  status: PhoneStatus;
  incomingCall: IncomingCallInfo | null;
  currentSession: RTCSession | null;
  answer: () => void;
  hangup: () => void;
  call: (target: string) => void;
  sendDTMF: (digit: string) => void;
  mute: () => void;
  unmute: () => void;
  isMuted: boolean;
  // Transfer capabilities
  hold: () => void;
  unhold: () => void;
  isOnHold: boolean;
  blindTransfer: (target: string) => void;
  attendedTransfer: (target: string) => Promise<void>;
  consultSession: RTCSession | null;
  completeAttendedTransfer: () => void;
  cancelAttendedTransfer: () => void;
}

// Build ICE servers array based on config
function getIceServers(config: PhoneConfig): RTCIceServer[] {
  if (config.vpnMode) {
    // VPN mode: no ICE servers - use host candidates only (direct VPN IP)
    return [];
  }

  const servers: RTCIceServer[] = [];

  if (config.stunServer) {
    servers.push({ urls: `stun:${config.stunServer}` });
  }

  // Only add TURN if server AND credentials are provided
  if (config.turnServer && config.turnUser && config.turnPass) {
    servers.push({
      urls: `turn:${config.turnServer}`,
      username: config.turnUser,
      credential: config.turnPass,
    });
  }

  // Fallback to Google STUN if no servers configured
  if (servers.length === 0) {
    servers.push(
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    );
  }

  return servers;
}

function getPcConfig(config: PhoneConfig): RTCConfiguration {
  return {
    iceServers: getIceServers(config),
    // In VPN mode, 'all' still works but no STUN servers means only host candidates
    iceTransportPolicy: 'all',
    bundlePolicy: 'max-bundle',
  };
}

// Enhanced audio attachment with AudioContext fallback
function attachRemoteAudio(
  session: RTCSession,
  audioEl: HTMLAudioElement | null,
  audioCtxRef: React.MutableRefObject<AudioContext | null>
) {
  console.log('[usePhone] attachRemoteAudio called, audioEl:', !!audioEl);

  const pc = (session as any).connection as RTCPeerConnection | undefined;
  if (!pc) {
    console.warn('[usePhone] No peerconnection available yet');
    return;
  }
  console.log('[usePhone] PeerConnection state:', pc.connectionState, 'ICE:', pc.iceConnectionState);

  const setupAudio = (stream: MediaStream, source: string) => {
    console.log(`[usePhone] setupAudio from ${source}, tracks:`, stream.getTracks().map(t => `${t.kind}:${t.readyState}:muted=${t.muted}`));

    // Method 1: Audio element
    if (audioEl) {
      audioEl.srcObject = stream;
      audioEl.volume = 1.0;
      audioEl.muted = false;
      console.log('[usePhone] Audio element configured, playing...');

      audioEl.play().then(() => {
        console.log('[usePhone] Audio element playback started successfully');
      }).catch((err) => {
        console.error('[usePhone] Audio element play error:', err);
      });
    } else {
      console.warn('[usePhone] No audio element available!');
    }

    // Method 2: AudioContext (always try as backup)
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
        console.log('[usePhone] Created new AudioContext');
      }
      const ctx = audioCtxRef.current;
      console.log('[usePhone] AudioContext state:', ctx.state);

      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          console.log('[usePhone] AudioContext resumed');
        });
      }

      const streamSource = ctx.createMediaStreamSource(stream);
      streamSource.connect(ctx.destination);
      console.log('[usePhone] AudioContext: connected stream to destination');
    } catch (err) {
      console.warn('[usePhone] AudioContext setup failed:', err);
    }
  };

  // Handle track event
  console.log('[usePhone] Adding track event listener to PC');
  pc.addEventListener('track', (evt: RTCTrackEvent) => {
    console.log('[usePhone] === TRACK EVENT ===');
    console.log('[usePhone] Track kind:', evt.track?.kind, 'readyState:', evt.track?.readyState);
    console.log('[usePhone] Track enabled:', evt.track?.enabled, 'muted:', evt.track?.muted);
    console.log('[usePhone] Streams count:', evt.streams?.length);

    if (evt.streams && evt.streams[0]) {
      setupAudio(evt.streams[0], 'track-event');

      // Monitor track unmute - some browsers start with muted tracks
      evt.track.onunmute = () => {
        console.log('[usePhone] === TRACK UNMUTED ===');
        const postUnmuteStream = new MediaStream([evt.track]);
        setupAudio(postUnmuteStream, 'track-unmute');
      };
    } else {
      console.warn('[usePhone] No streams in track event!');
    }
  });

  // Also set ontrack directly (some browsers prefer this)
  pc.ontrack = (evt: RTCTrackEvent) => {
    console.log('[usePhone] === ONTRACK (direct) ===');
    if (evt.streams && evt.streams[0]) {
      setupAudio(evt.streams[0], 'ontrack-direct');
    }
  };

  // Fallback: check existing receivers
  const receivers = pc.getReceivers?.();
  console.log('[usePhone] Checking receivers:', receivers?.length || 0);
  if (receivers && receivers.length > 0) {
    const audioReceiver = receivers.find((r) => r.track && r.track.kind === 'audio');
    if (audioReceiver) {
      console.log('[usePhone] Found existing audio receiver, track:', audioReceiver.track.readyState);
      const stream = new MediaStream([audioReceiver.track]);
      setupAudio(stream, 'existing-receiver');
    }
  }
}

export function usePhone({ config, onIncomingCall, remoteAudioRef }: UsePhoneOptions): UsePhoneReturn {
  const [status, setStatus] = useState<PhoneStatus>('idle');
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  const [currentSession, setCurrentSession] = useState<RTCSession | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [consultSession, setConsultSession] = useState<RTCSession | null>(null);

  const uaRef = useRef<JsSIP.UA | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!config) {
      setStatus('idle');
      return;
    }

    // Disable JsSIP debug logging in production
    JsSIP.debug.enable('JsSIP:*'); // DEBUG

    const socket = new JsSIP.WebSocketInterface(config.wsServer);

    const ua = new JsSIP.UA({
      sockets: [socket],
      uri: config.sipUri,
      authorization_user: config.sipUri.replace('sip:', '').split('@')[0], // Extract user from URI
      password: config.sipPassword,
      display_name: config.displayName || 'Support Agent',
      register: true,
      register_expires: 60,
      session_timers: false,
      connection_recovery_min_interval: 4,
      connection_recovery_max_interval: 30,
    });

    uaRef.current = ua;
    setStatus('registering');

    ua.on('connecting', () => { console.log('[usePhone] WebSocket connecting...'); if (status !== 'in_call') setStatus('registering'); });
    ua.on('connected', () => { console.log('[usePhone] WebSocket connected'); setStatus('registering'); });
    ua.on('registered', () => {
      console.log('[usePhone] SIP registered');
      setStatus('registered');
    });
    ua.on('unregistered', () => {
      console.log('[usePhone] SIP unregistered');
      setStatus('idle');
    });
    ua.on('registrationFailed', (e: any) => {
      console.error('[usePhone] Registration failed:', e?.cause);
      setStatus('failed');
    });
    ua.on('disconnected', () => { console.log('[usePhone] WebSocket disconnected'); if (status !== 'in_call') setStatus('disconnected'); });

    ua.on('newRTCSession', (data: { session: RTCSession; originator: string }) => {
      const session = data.session;
      console.log('[usePhone] newRTCSession, direction:', data.originator);

      if (data.originator === 'remote') {
        // Incoming call
        const callerNumber =
          session.remote_identity?.uri?.user ||
          session.remote_identity?.display_name ||
          'Unknown';

        // Pre-acquire mic immediately for faster answer
        navigator.mediaDevices.getUserMedia({ audio: true, video: false })
          .then((stream) => {
            localStreamRef.current = stream;
          })
          .catch((err) => console.warn('[usePhone] Mic pre-acquire failed:', err));

        setIncomingCall({ session, callerNumber });
        onIncomingCall?.(session, callerNumber);

        // Attach peerconnection event handler for incoming calls
        session.on('peerconnection', (e: any) => {
          console.log('[usePhone] PeerConnection created (incoming)');
          const pc = e.peerconnection as RTCPeerConnection;

          pc.ontrack = (trackEvent) => {
            console.log('[usePhone] ontrack (incoming):', trackEvent.streams?.length, 'streams');
            if (trackEvent.streams[0] && remoteAudioRef?.current) {
              const audioEl = remoteAudioRef.current;
              audioEl.srcObject = trackEvent.streams[0];
              audioEl.volume = 1.0;
              audioEl.muted = false;
              audioEl.play().catch((err) => console.error('[usePhone] Audio play error:', err));

              // Track unmute handler
              trackEvent.track.onunmute = () => {
                console.log('[usePhone] Track unmuted');
                const stream = new MediaStream([trackEvent.track]);
                audioEl.srcObject = stream;
                audioEl.play().catch(() => {});

                // AudioContext fallback after unmute
                try {
                  if (!audioCtxRef.current) {
                    audioCtxRef.current = new AudioContext();
                  }
                  const ctx = audioCtxRef.current;
                  const source = ctx.createMediaStreamSource(stream);
                  source.connect(ctx.destination);
                } catch (err) {
                  console.warn('[usePhone] Post-unmute AudioContext error:', err);
                }
              };
            }
          };

          pc.onicecandidate = (event) => {
            if (event.candidate) {
              console.log('[usePhone] ICE candidate:', event.candidate.type, event.candidate.address);
            }
          };

          pc.oniceconnectionstatechange = () => {
            console.log('[usePhone] ICE state:', pc.iceConnectionState);
          };
        });

        session.on('ended', () => {
          console.log('[usePhone] Session ended');
          setIncomingCall(null);
          setCurrentSession(null);
          setStatus('registered');
          setIsMuted(false);
          localStreamRef.current?.getTracks().forEach((t) => t.stop());
          localStreamRef.current = null;
        });

        session.on('failed', (e: any) => {
          console.log('[usePhone] Session failed:', e?.cause);
          setIncomingCall(null);
          setCurrentSession(null);
          setStatus('registered');
          setIsMuted(false);
          localStreamRef.current?.getTracks().forEach((t) => t.stop());
          localStreamRef.current = null;
        });

        session.on('accepted', () => {
          console.log('[usePhone] Session accepted');
          setStatus('in_call');
          setIncomingCall(null);
          setCurrentSession(session);
        });

        session.on('confirmed', () => {
          console.log('[usePhone] Session confirmed');
          attachRemoteAudio(session, remoteAudioRef?.current ?? null, audioCtxRef);
        });
      } else {
        // Outgoing call
        setCurrentSession(session);
        setStatus('in_call');

        session.on('peerconnection', () => {
          attachRemoteAudio(session, remoteAudioRef?.current ?? null, audioCtxRef);
        });

        session.on('accepted', () => {
          attachRemoteAudio(session, remoteAudioRef?.current ?? null, audioCtxRef);
        });

        session.on('confirmed', () => {
          attachRemoteAudio(session, remoteAudioRef?.current ?? null, audioCtxRef);
        });

        session.on('ended', () => {
          setCurrentSession(null);
          setStatus('registered');
          setIsMuted(false);
          localStreamRef.current?.getTracks().forEach((t) => t.stop());
          localStreamRef.current = null;
        });

        session.on('failed', () => {
          setCurrentSession(null);
          setStatus('registered');
          setIsMuted(false);
        });
      }
    });

    ua.start();

    return () => {
      try {
        ua.stop();
      } catch (_) {}
      uaRef.current = null;
      audioCtxRef.current?.close();
      audioCtxRef.current = null;
    };
  }, [config?.sipUri, config?.sipPassword, config?.wsServer, config?.vpnMode]);

  const answer = useCallback(async () => {
    if (!incomingCall || !config) return;

    try {
      // Use pre-acquired stream if ready, otherwise request now
      if (!localStreamRef.current) {
        localStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      }

      const pcConfig = getPcConfig(config);
      console.log('[usePhone] Answering with ICE config:', pcConfig);

      incomingCall.session.answer({
        mediaStream: localStreamRef.current,
        mediaConstraints: { audio: true, video: false },
        pcConfig,
      });

      // Attach remote audio immediately
      attachRemoteAudio(incomingCall.session, remoteAudioRef?.current ?? null, audioCtxRef);
    } catch (err) {
      console.error('[usePhone] Answer failed:', err);
    }
  }, [incomingCall, config, remoteAudioRef]);

  const hangup = useCallback(() => {
    if (currentSession) {
      try {
        currentSession.terminate();
      } catch (_) {}
    }
    if (incomingCall) {
      try {
        incomingCall.session.terminate();
      } catch (_) {}
      setIncomingCall(null);
    }
    setCurrentSession(null);
    setStatus('registered');
    setIsMuted(false);
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
  }, [currentSession, incomingCall]);

  const call = useCallback(
    async (target: string) => {
      if (!uaRef.current || !config || status !== 'registered') return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;

        const targetUri = target.startsWith('sip:')
          ? target
          : `sip:${target}@${config.sipDomain}`;

        const pcConfig = getPcConfig(config);
        console.log('[usePhone] Calling', targetUri, 'with ICE config:', pcConfig);

        uaRef.current.call(targetUri, {
          mediaConstraints: { audio: true, video: false },
          pcConfig,
        });
      } catch (err) {
        console.error('[usePhone] Call failed:', err);
      }
    },
    [status, config]
  );

  const sendDTMF = useCallback(
    (digit: string) => {
      if (currentSession) {
        try {
          currentSession.sendDTMF(digit);
        } catch (err) {
          console.error('[usePhone] DTMF failed:', err);
        }
      }
    },
    [currentSession]
  );

  const mute = useCallback(() => {
    if (currentSession) {
      try {
        currentSession.mute({ audio: true });
        setIsMuted(true);
      } catch (err) {
        console.error('[usePhone] Mute failed:', err);
      }
    }
  }, [currentSession]);

  const unmute = useCallback(() => {
    if (currentSession) {
      try {
        currentSession.unmute({ audio: true });
        setIsMuted(false);
      } catch (err) {
        console.error('[usePhone] Unmute failed:', err);
      }
    }
  }, [currentSession]);

  // Hold current call
  const hold = useCallback(() => {
    if (currentSession) {
      try {
        currentSession.hold();
        setIsOnHold(true);
        console.log('[usePhone] Call placed on hold');
      } catch (err) {
        console.error('[usePhone] Hold failed:', err);
      }
    }
  }, [currentSession]);

  // Unhold current call
  const unhold = useCallback(() => {
    if (currentSession) {
      try {
        currentSession.unhold();
        setIsOnHold(false);
        console.log('[usePhone] Call resumed from hold');
      } catch (err) {
        console.error('[usePhone] Unhold failed:', err);
      }
    }
  }, [currentSession]);

  // Blind transfer - immediately transfers the call
  const blindTransfer = useCallback(
    (target: string) => {
      if (!currentSession || !config) {
        console.error('[usePhone] No active session for transfer');
        return;
      }

      try {
        const targetUri = target.startsWith('sip:')
          ? target
          : `sip:${target}@${config.sipDomain}`;

        console.log('[usePhone] Blind transfer to:', targetUri);

        // JsSIP refer method for blind transfer
        currentSession.refer(targetUri, {
          eventHandlers: {
            requestSucceeded: () => {
              console.log('[usePhone] Transfer REFER accepted');
            },
            requestFailed: (e: any) => {
              console.error('[usePhone] Transfer REFER failed:', e?.cause);
            },
            accepted: () => {
              console.log('[usePhone] Transfer accepted by target');
              // Call will be terminated by Asterisk after successful transfer
            },
            failed: (e: any) => {
              console.error('[usePhone] Transfer failed:', e?.cause);
            },
          },
        });
      } catch (err) {
        console.error('[usePhone] Blind transfer error:', err);
      }
    },
    [currentSession, config]
  );

  // Attended transfer - puts current call on hold, calls target, then transfers
  const attendedTransfer = useCallback(
    async (target: string) => {
      if (!currentSession || !config || !uaRef.current) {
        console.error('[usePhone] No active session for attended transfer');
        return;
      }

      try {
        // Step 1: Put current call on hold
        currentSession.hold();
        setIsOnHold(true);
        console.log('[usePhone] Original call on hold for attended transfer');

        // Step 2: Make consultation call
        const targetUri = target.startsWith('sip:')
          ? target
          : `sip:${target}@${config.sipDomain}`;

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const pcConfig = getPcConfig(config);

        console.log('[usePhone] Starting consultation call to:', targetUri);

        const consultCall = uaRef.current.call(targetUri, {
          mediaConstraints: { audio: true, video: false },
          pcConfig,
        });

        // Store consult session for later
        setConsultSession(consultCall);

        consultCall.on('accepted', () => {
          console.log('[usePhone] Consultation call connected');
          attachRemoteAudio(consultCall, remoteAudioRef?.current ?? null, audioCtxRef);
        });

        consultCall.on('failed', (e: any) => {
          console.error('[usePhone] Consultation call failed:', e?.cause);
          // Resume original call
          currentSession.unhold();
          setIsOnHold(false);
          setConsultSession(null);
        });

        consultCall.on('ended', () => {
          console.log('[usePhone] Consultation call ended');
          setConsultSession(null);
        });
      } catch (err) {
        console.error('[usePhone] Attended transfer error:', err);
        // Try to restore original call
        try {
          currentSession.unhold();
          setIsOnHold(false);
        } catch (_) {}
      }
    },
    [currentSession, config, remoteAudioRef]
  );

  // Complete attended transfer - transfers original call to consult party
  const completeAttendedTransfer = useCallback(() => {
    if (!currentSession || !consultSession || !config) {
      console.error('[usePhone] Cannot complete transfer - missing sessions');
      return;
    }

    try {
      // Get the consult call's remote identity to transfer to
      const targetUri = consultSession.remote_identity?.uri?.toString() ||
        `sip:${consultSession.remote_identity?.uri?.user}@${config.sipDomain}`;

      console.log('[usePhone] Completing attended transfer to:', targetUri);

      // Refer the original call to the target with replaces header
      currentSession.refer(targetUri, {
        replaces: consultSession,
        eventHandlers: {
          requestSucceeded: () => {
            console.log('[usePhone] Attended transfer REFER accepted');
          },
          requestFailed: (e: any) => {
            console.error('[usePhone] Attended transfer REFER failed:', e?.cause);
          },
          accepted: () => {
            console.log('[usePhone] Attended transfer completed');
            // Hang up consultation call
            try {
              consultSession.terminate();
            } catch (_) {}
            setConsultSession(null);
          },
          failed: (e: any) => {
            console.error('[usePhone] Attended transfer failed:', e?.cause);
          },
        },
      });
    } catch (err) {
      console.error('[usePhone] Complete transfer error:', err);
    }
  }, [currentSession, consultSession, config]);

  // Cancel attended transfer - hang up consult call, resume original
  const cancelAttendedTransfer = useCallback(() => {
    if (consultSession) {
      try {
        consultSession.terminate();
        console.log('[usePhone] Consultation call terminated');
      } catch (_) {}
      setConsultSession(null);
    }

    if (currentSession && isOnHold) {
      try {
        currentSession.unhold();
        setIsOnHold(false);
        console.log('[usePhone] Original call resumed');
        // Re-attach audio
        attachRemoteAudio(currentSession, remoteAudioRef?.current ?? null, audioCtxRef);
      } catch (err) {
        console.error('[usePhone] Failed to resume original call:', err);
      }
    }
  }, [consultSession, currentSession, isOnHold, remoteAudioRef]);

  return {
    status,
    incomingCall,
    currentSession,
    answer,
    hangup,
    call,
    sendDTMF,
    mute,
    unmute,
    isMuted,
    // Transfer capabilities
    hold,
    unhold,
    isOnHold,
    blindTransfer,
    attendedTransfer,
    consultSession,
    completeAttendedTransfer,
    cancelAttendedTransfer,
  };
}
